import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api';

interface FormStyles {
  backgroundType: 'color' | 'gradient' | 'image' | 'pattern';
  backgroundColor: string;
  backgroundGradient: { from: string; to: string; direction: string } | null;
  backgroundImageUrl: string | null;
  backgroundPattern: string | null;
  backgroundOpacity: number;
  fontFamily: string;
  fontSizeBase: number;
  primaryColor: string;
  secondaryColor: string;
  textColor: string;
  accentColor: string;
  formWidth: 'narrow' | 'medium' | 'wide' | 'full';
  formPadding: 'compact' | 'normal' | 'relaxed';
  borderRadius: 'none' | 'small' | 'medium' | 'large' | 'full';
  shadow: 'none' | 'small' | 'medium' | 'large';
}

interface MobileFormStylingProps {
  formId: string;
  isOpen: boolean;
  onClose: () => void;
}

const FONT_OPTIONS = [
  { value: 'Inter', label: 'Inter' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Nunito', label: 'Nunito' },
  { value: 'Merriweather', label: 'Merriweather' },
  { value: 'Playfair Display', label: 'Playfair Display' },
];

const GRADIENT_DIRECTIONS = [
  { value: 'to-t', label: 'Up' },
  { value: 'to-b', label: 'Down' },
  { value: 'to-l', label: 'Left' },
  { value: 'to-r', label: 'Right' },
  { value: 'to-tr', label: 'Diagonal ↗' },
  { value: 'to-bl', label: 'Diagonal ↙' },
];

const PATTERNS = [
  { value: 'dots', label: 'Dots' },
  { value: 'grid', label: 'Grid' },
  { value: 'lines', label: 'Lines' },
  { value: 'waves', label: 'Waves' },
  { value: 'zigzag', label: 'Zigzag' },
];

const DEFAULT_STYLES: FormStyles = {
  backgroundType: 'color',
  backgroundColor: '#ffffff',
  backgroundGradient: null,
  backgroundImageUrl: null,
  backgroundPattern: null,
  backgroundOpacity: 100,
  fontFamily: 'Inter',
  fontSizeBase: 16,
  primaryColor: '#3b82f6',
  secondaryColor: '#6366f1',
  textColor: '#1f2937',
  accentColor: '#8b5cf6',
  formWidth: 'medium',
  formPadding: 'normal',
  borderRadius: 'medium',
  shadow: 'medium',
};

type TabId = 'background' | 'typography' | 'colors' | 'layout';

export default function MobileFormStyling({ formId, isOpen, onClose }: MobileFormStylingProps) {
  const { t } = useTranslation('mobile');
  const [styles, setStyles] = useState<FormStyles>(DEFAULT_STYLES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('background');

  useEffect(() => {
    if (isOpen && formId) {
      loadStyles();
    }
  }, [isOpen, formId]);

  async function loadStyles() {
    try {
      setLoading(true);
      const response = await api.get(`/api/forms/${formId}/styles`);
      if (response.ok) {
        const data = await response.json() as { styles: FormStyles };
        setStyles({ ...DEFAULT_STYLES, ...data.styles });
      }
    } catch (error) {
      console.error('Failed to load styles:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    try {
      setSaving(true);
      const response = await api.put(`/api/forms/${formId}/styles`, styles);
      if (response.ok) {
        onClose();
      }
    } catch (error) {
      console.error('Failed to save styles:', error);
    } finally {
      setSaving(false);
    }
  }

  function updateStyles(updates: Partial<FormStyles>) {
    setStyles((prev) => ({ ...prev, ...updates }));
  }

  if (!isOpen) return null;

  const tabs: { id: TabId; label: string; icon: string }[] = [
    { id: 'background', label: t('styling.tabs.background'), icon: '🎨' },
    { id: 'typography', label: t('styling.tabs.typography'), icon: '🔤' },
    { id: 'colors', label: t('styling.tabs.colors'), icon: '🌈' },
    { id: 'layout', label: t('styling.tabs.layout'), icon: '📐' },
  ];

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50 animate-fadeIn" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 h-[90%] bg-white dark:bg-gray-900 rounded-t-3xl shadow-2xl animate-slideUp flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-center py-2">
            <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full absolute top-2 left-1/2 -translate-x-1/2" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('styling.title')}</h2>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-xl font-medium text-sm active:scale-95 transition-transform disabled:opacity-50"
          >
            {saving ? t('common.loading') : t('common.save')}
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-10 h-10 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Tab Navigation */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 px-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex flex-col items-center gap-1 py-3 text-xs font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <span className="text-lg">{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 pb-safe">
              {activeTab === 'background' && (
                <div className="space-y-6">
                  {/* Background Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      {t('styling.background.type')}
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {(['color', 'gradient', 'image', 'pattern'] as const).map((type) => (
                        <button
                          key={type}
                          onClick={() => updateStyles({ backgroundType: type })}
                          className={`px-3 py-3 rounded-xl text-xs font-medium capitalize transition-all touch-manipulation ${
                            styles.backgroundType === type
                              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 ring-2 ring-primary-500'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {t(`styling.background.types.${type}`)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Color Picker */}
                  {styles.backgroundType === 'color' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        {t('styling.background.color')}
                      </label>
                      <div className="flex items-center gap-4">
                        <input
                          type="color"
                          value={styles.backgroundColor}
                          onChange={(e) => updateStyles({ backgroundColor: e.target.value })}
                          className="w-16 h-16 rounded-xl cursor-pointer border-2 border-gray-200 dark:border-gray-700"
                        />
                        <input
                          type="text"
                          value={styles.backgroundColor}
                          onChange={(e) => updateStyles({ backgroundColor: e.target.value })}
                          className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-800 rounded-xl text-gray-900 dark:text-white font-mono text-sm"
                        />
                      </div>
                    </div>
                  )}

                  {/* Gradient */}
                  {styles.backgroundType === 'gradient' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                            {t('styling.background.gradientFrom')}
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={styles.backgroundGradient?.from || '#3b82f6'}
                              onChange={(e) =>
                                updateStyles({
                                  backgroundGradient: {
                                    from: e.target.value,
                                    to: styles.backgroundGradient?.to || '#8b5cf6',
                                    direction: styles.backgroundGradient?.direction || 'to-b',
                                  },
                                })
                              }
                              className="w-12 h-12 rounded-lg cursor-pointer"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                            {t('styling.background.gradientTo')}
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={styles.backgroundGradient?.to || '#8b5cf6'}
                              onChange={(e) =>
                                updateStyles({
                                  backgroundGradient: {
                                    from: styles.backgroundGradient?.from || '#3b82f6',
                                    to: e.target.value,
                                    direction: styles.backgroundGradient?.direction || 'to-b',
                                  },
                                })
                              }
                              className="w-12 h-12 rounded-lg cursor-pointer"
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                          {t('styling.background.direction')}
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          {GRADIENT_DIRECTIONS.map((dir) => (
                            <button
                              key={dir.value}
                              onClick={() =>
                                updateStyles({
                                  backgroundGradient: {
                                    from: styles.backgroundGradient?.from || '#3b82f6',
                                    to: styles.backgroundGradient?.to || '#8b5cf6',
                                    direction: dir.value,
                                  },
                                })
                              }
                              className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                styles.backgroundGradient?.direction === dir.value
                                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                              }`}
                            >
                              {dir.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      {/* Preview */}
                      <div
                        className="h-20 rounded-xl border border-gray-200 dark:border-gray-700"
                        style={{
                          background: `linear-gradient(${
                            styles.backgroundGradient?.direction === 'to-t' ? 'to top' :
                            styles.backgroundGradient?.direction === 'to-b' ? 'to bottom' :
                            styles.backgroundGradient?.direction === 'to-l' ? 'to left' :
                            styles.backgroundGradient?.direction === 'to-r' ? 'to right' :
                            styles.backgroundGradient?.direction === 'to-tr' ? 'to top right' :
                            'to bottom left'
                          }, ${styles.backgroundGradient?.from || '#3b82f6'}, ${styles.backgroundGradient?.to || '#8b5cf6'})`,
                        }}
                      />
                    </div>
                  )}

                  {/* Image URL */}
                  {styles.backgroundType === 'image' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        {t('styling.background.imageUrl')}
                      </label>
                      <input
                        type="url"
                        value={styles.backgroundImageUrl || ''}
                        onChange={(e) => updateStyles({ backgroundImageUrl: e.target.value || null })}
                        placeholder="https://example.com/image.jpg"
                        className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 rounded-xl text-gray-900 dark:text-white text-sm"
                      />
                      {styles.backgroundImageUrl && (
                        <div
                          className="mt-4 h-32 rounded-xl bg-cover bg-center border border-gray-200 dark:border-gray-700"
                          style={{ backgroundImage: `url(${styles.backgroundImageUrl})` }}
                        />
                      )}
                    </div>
                  )}

                  {/* Pattern */}
                  {styles.backgroundType === 'pattern' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        {t('styling.background.pattern')}
                      </label>
                      <div className="grid grid-cols-5 gap-2">
                        {PATTERNS.map((pattern) => (
                          <button
                            key={pattern.value}
                            onClick={() => updateStyles({ backgroundPattern: pattern.value })}
                            className={`px-2 py-3 rounded-xl text-xs font-medium transition-all ${
                              styles.backgroundPattern === pattern.value
                                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 ring-2 ring-primary-500'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                            }`}
                          >
                            {pattern.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Opacity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      {t('styling.background.opacity')}: {styles.backgroundOpacity}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={styles.backgroundOpacity}
                      onChange={(e) => updateStyles({ backgroundOpacity: parseInt(e.target.value) })}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>
              )}

              {activeTab === 'typography' && (
                <div className="space-y-6">
                  {/* Font Family */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      {t('styling.typography.fontFamily')}
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {FONT_OPTIONS.map((font) => (
                        <button
                          key={font.value}
                          onClick={() => updateStyles({ fontFamily: font.value })}
                          style={{ fontFamily: font.value }}
                          className={`px-3 py-3 rounded-xl text-xs font-medium transition-all ${
                            styles.fontFamily === font.value
                              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 ring-2 ring-primary-500'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {font.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Font Size */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      {t('styling.typography.fontSize')}: {styles.fontSizeBase}px
                    </label>
                    <input
                      type="range"
                      min="12"
                      max="24"
                      value={styles.fontSizeBase}
                      onChange={(e) => updateStyles({ fontSizeBase: parseInt(e.target.value) })}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  {/* Preview */}
                  <div
                    className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
                    style={{ fontFamily: styles.fontFamily, fontSize: styles.fontSizeBase }}
                  >
                    <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">{t('styling.typography.previewHeading')}</h3>
                    <p className="text-gray-600 dark:text-gray-400">{t('styling.typography.previewText')}</p>
                  </div>
                </div>
              )}

              {activeTab === 'colors' && (
                <div className="space-y-6">
                  {[
                    { key: 'primaryColor', label: t('styling.colors.primary'), desc: t('styling.colors.primaryDesc') },
                    { key: 'secondaryColor', label: t('styling.colors.secondary'), desc: t('styling.colors.secondaryDesc') },
                    { key: 'textColor', label: t('styling.colors.text'), desc: t('styling.colors.textDesc') },
                    { key: 'accentColor', label: t('styling.colors.accent'), desc: t('styling.colors.accentDesc') },
                  ].map((color) => (
                    <div key={color.key} className="flex items-center gap-4">
                      <input
                        type="color"
                        value={styles[color.key as keyof FormStyles] as string}
                        onChange={(e) => updateStyles({ [color.key]: e.target.value })}
                        className="w-14 h-14 rounded-xl cursor-pointer border-2 border-gray-200 dark:border-gray-700 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white">{color.label}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{color.desc}</p>
                      </div>
                      <input
                        type="text"
                        value={styles[color.key as keyof FormStyles] as string}
                        onChange={(e) => updateStyles({ [color.key]: e.target.value })}
                        className="w-24 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-xs font-mono text-gray-900 dark:text-white"
                      />
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'layout' && (
                <div className="space-y-6">
                  {/* Form Width */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      {t('styling.layout.width')}
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {([
                        { value: 'narrow', label: t('styling.layout.widths.narrow') },
                        { value: 'medium', label: t('styling.layout.widths.medium') },
                        { value: 'wide', label: t('styling.layout.widths.wide') },
                        { value: 'full', label: t('styling.layout.widths.full') },
                      ] as const).map((option) => (
                        <button
                          key={option.value}
                          onClick={() => updateStyles({ formWidth: option.value })}
                          className={`px-3 py-3 rounded-xl text-xs font-medium transition-all ${
                            styles.formWidth === option.value
                              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 ring-2 ring-primary-500'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Padding */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      {t('styling.layout.padding')}
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {([
                        { value: 'compact', label: t('styling.layout.paddings.compact') },
                        { value: 'normal', label: t('styling.layout.paddings.normal') },
                        { value: 'relaxed', label: t('styling.layout.paddings.relaxed') },
                      ] as const).map((option) => (
                        <button
                          key={option.value}
                          onClick={() => updateStyles({ formPadding: option.value })}
                          className={`px-3 py-3 rounded-xl text-xs font-medium transition-all ${
                            styles.formPadding === option.value
                              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 ring-2 ring-primary-500'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Border Radius */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      {t('styling.layout.borderRadius')}
                    </label>
                    <div className="grid grid-cols-5 gap-2">
                      {(['none', 'small', 'medium', 'large', 'full'] as const).map((radius) => (
                        <button
                          key={radius}
                          onClick={() => updateStyles({ borderRadius: radius })}
                          className={`px-2 py-3 rounded-xl text-xs font-medium capitalize transition-all ${
                            styles.borderRadius === radius
                              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 ring-2 ring-primary-500'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {t(`styling.layout.radii.${radius}`)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Shadow */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      {t('styling.layout.shadow')}
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {(['none', 'small', 'medium', 'large'] as const).map((shadow) => (
                        <button
                          key={shadow}
                          onClick={() => updateStyles({ shadow })}
                          className={`px-3 py-3 rounded-xl text-xs font-medium capitalize transition-all ${
                            styles.shadow === shadow
                              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 ring-2 ring-primary-500'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {t(`styling.layout.shadows.${shadow}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
